import { Image, useTranslations } from '@/lib/next-shim'
import { Link } from '@/lib/i18n/settings'

import Container from '@/components/ui/layouts/Container'
import { MeetingLinksAutocomplete } from '@/features/autocomplete'
import Marketing from '@/assets/img/marketing.jpg'
import MeetingLinks from '../MeetingLinks'
import { Session } from '@/lib/next-shim'
import { getEnv } from '@/lib/env'

type HeroProps = {
  session?: Session
}

export default function Hero(props: HeroProps) {
  const t = useTranslations()
  const { session } = props

  // Get customizable content from environment variables with fallbacks
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')
  const heroTitle = getEnv('NEXT_PUBLIC_HERO_TITLE', t('marketing.sections.Hero.title'))
  const heroSubtitle = getEnv('NEXT_PUBLIC_HERO_SUBTITLE', t('marketing.sections.Hero.subtitle'))
  const heroCtaText = getEnv('NEXT_PUBLIC_HERO_CTA_TEXT', t('marketing.sections.Hero.button.signUp.label'))
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO', '')

  return (
    <Container className="flex flex-col items-center pt-4 pb-4 sm:pt-10 sm:py-10 lg:pt-16 lg:py-16 bg-white">
      <div className="lg:grid lg:grid-cols-12 gap-8">
        <div className="sm:text-center md:mx-auto md:max-w-2xl lg:col-span-6 lg:text-left">
          {/* Custom logo if provided */}
          {brandLogo && (
            <div className="mb-6">
              <Image src={brandLogo} alt={brandName} width={142} height={80} className="h-20 w-auto object-contain" />
            </div>
          )}

          <h1>
            <span className="block text-primary font-semibold text-lg mb-2">{brandName}</span>
            <span className="mt-1 block text-4xl font-bold tracking-tight sm:text-5xl xl:text-6xl">
              <span className="block text-gray-900">{heroTitle}</span>
            </span>
          </h1>
          <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">{heroSubtitle}</p>

          {/* Benefits Badges */}
          <div className="mt-6 flex flex-wrap gap-3 justify-center lg:justify-start">
            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              <svg className="mr-1 h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Video Consultations
            </span>
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
              <svg className="mr-1 h-3 w-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              In-person Visits
            </span>
            <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">
              <svg className="mr-1 h-3 w-3 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Second Opinions
            </span>
          </div>

          <div className="mt-8 sm:mx-auto sm:max-w-lg sm:text-center lg:mx-0 lg:text-left">
            <div className="w-full inline-block md:hidden sm:inline-block">
              <MeetingLinks />
            </div>
            <p className="text-base font-medium text-gray-900">{t('marketing.sections.Hero.searchBar.title')}</p>
            <div className="mt-3" data-testid="autocomplete">
              <MeetingLinksAutocomplete type="header" />
            </div>
            <p className="mt-2 text-sm text-gray-500">Find your doctor or specialist by name, specialty, or location</p>
          </div>

          <div className="hidden mt-10 sm:flex items-center max-lg:justify-center gap-x-6">
            {session ? (
              <Link href="/account/dashboard" className="ds-btn ds-btn-primary">
                {t('marketing.sections.Hero.button.accessMySpace.label')}
              </Link>
            ) : (
              <>
                <Link href="/register" className="ds-btn ds-btn-primary">
                  {heroCtaText}
                </Link>
                <Link href="/login" className="ds-btn ds-btn-primary ds-btn-outline">
                  {t('marketing.sections.Hero.button.signIn.label')}
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="relative mt-8 lg:mt-0 lg:col-span-6 lg:flex lg:items-center">
          <div className="hidden lg:block rounded-2xl overflow-hidden shadow-xl border border-gray-200">
            <Image src={Marketing} alt="Telemedicine consultation" width={600} height={400} className="w-full h-auto object-cover" />
          </div>
        </div>
        <div className="col-span-12 hidden sm:hidden md:inline-block">
          <MeetingLinks />
        </div>
      </div>
    </Container>
  )
}
